import { message } from "ant-design-vue";
import CryptoJS from "crypto-js";
import * as xlsx from "xlsx";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import useAccount from "@/store/modules/account";

// 获取数据类型
export function getObjType(data: any) {
  const toString = Object.prototype.toString;
  const map: Record<string, string> = {
    "[object Boolean]": "boolean",
    "[object Number]": "number",
    "[object String]": "string",
    "[object Function]": "function",
    "[object Array]": "array",
    "[object Date]": "date",
    "[object RegExp]": "regExp",
    "[object Undefined]": "undefined",
    "[object Null]": "null",
    "[object Object]": "object"
  };
  if (data instanceof Element) {
    return "element";
  }
  return map[toString.call(data)];
}

// 将对象转为query参数
export function convertQuery(data: any) {
  const arr: string[] = [];
  Object.keys(data).forEach((key: string) => {
    if (data[key]) {
      arr.push(`${key}=${data[key]}`);
    }
  });

  return arr.join("&");
}

export function queryConvertObject(query: string) {
  const str = query.includes("?") ? query.split("?")[1] : query;
  return str.split("&").reduce((map: any, item) => {
    const [key, value = ""] = item.split("=");
    map[key] = value;
    return map;
  }, {});
}

// 对象深拷贝
export function deepClone(data: any) {
  const dataType = getObjType(data);
  let obj: any;
  if (dataType === "array") {
    obj = [];
  } else if (dataType === "object") {
    obj = {};
  } else {
    // 不具备下一次递归条件
    return data;
  }

  if (dataType === "array") {
    data.forEach((item: any) => {
      obj.push(deepClone(item));
    });
  } else if (dataType === "object") {
    for (const key in data) {
      obj[key] = deepClone(data[key]);
    }
  }

  return obj;
}

// 随机生成N位数字
export function randomLenNum(length = 10, dateNow?: boolean) {
  let random: string;
  random = Math.ceil(Math.random() * 100000000000000)
    .toString()
    .substring(0, length);

  if (dateNow) {
    random = (Number(random) + Date.now()).toString();
  }
  return random;
}

// 随机生成N位字符
export function randomCode(length = 12) {
  let str = "";
  for (let i = 0; i <= length; i++) {
    str += String.fromCharCode(
      Math.floor(Math.random() * 26 + "a".charCodeAt(0))
    );
  }
  const arr: string[] = [];
  const result = str.split("").reduce((res, item, i) => {
    res.push(item);
    if (i % 2 == 0) {
      res.push(Math.ceil(Math.random() * 100).toString());
    }
    return res;
  }, arr);

  return result.join("");
}

// 防抖函数
export function debounce(fun: any, wait = 1000) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return function (this: any, ...args: any) {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      fun.apply(this, args);
    }, wait);
  };
}

// 复制内容
export function copyToClipboard(
  value: (string | number)[] | string,
  msg = "复制成功!",
  separator = ","
) {
  let str = "";
  if (typeof value === "string") {
    str = value;
  } else if (Array.isArray(value)) {
    str = value.join(separator);
  }
  // 复制兼容线上和本地环境 复制到剪贴板 兼容chrome、safari、移动端
  if (window.navigator.clipboard && window.isSecureContext) {
    return window.navigator.clipboard.writeText(str).then(() => {
      message.success(msg);
    });
  } else {
    const textArea = document.createElement("textarea");
    textArea.value = str;
    textArea.style.position = "absolute";
    textArea.style.opacity = "0";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    return new Promise((resolve, reject) => {
      document.execCommand("copy") ? resolve(true) : reject();
      textArea.remove();
      message.success(msg);
    });
  }
}

// 下载excel
/* export function download(
  file: string,
  fileName: string,
  fileType = "application/vnd.ms-excel"
) {
  try {
    const blob = new Blob([file], { type: fileType });
    const a = document.createElement("a");
    const url = window.URL || window.webkitURL;
    a.href = url.createObjectURL(blob);
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    url.revokeObjectURL(a.href);
    document.body.removeChild(a);
  } catch (err) {
    console.error("file download error", err);
  }
} */

// 下载文件
export function download(file: string, fileName: string, fileType = "") {
  try {
    fileType = fileType || getFileTypeByName(fileName);
    const blob = new Blob([file], { type: fileType });
    const a = document.createElement("a");
    const url = window.URL || window.webkitURL;
    a.href = url.createObjectURL(blob);
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    url.revokeObjectURL(a.href);
    document.body.removeChild(a);
  } catch (err) {
    console.error("file download error", err);
  }
}

export const getFileTypeByName = (fileName: string) => {
  const arr = fileName.split("."); // split后获得文件后缀
  const suffix = arr[arr.length - 1];
  let fileTypeMime = "";
  switch (suffix) {
    case "png":
      fileTypeMime = "image/png";
      break;
    case "doc":
      fileTypeMime = "application/msword";
      break;
    case "docx":
      fileTypeMime =
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      break;
    case "jpg":
    case "jpeg":
      fileTypeMime = "image/jpeg";
      break;
    case "gif":
      fileTypeMime = "image/gif";
      break;
    case "svg":
      fileTypeMime = "image/svg+xml";
      break;
    case "tif":
    case "tiff":
      fileTypeMime = "image/tiff";
      break;
    case "txt":
      fileTypeMime = "text/plain";
      break;
    case "ppt":
      fileTypeMime = "application/vnd.ms-powerpoint";
      break;
    case "pptx":
      fileTypeMime =
        "application/vnd.openxmlformats-officedocument.presentationml.presentation";
      break;
    case "xls":
      fileTypeMime = "application/vnd.ms-excel";
      break;
    case "xlsx":
      fileTypeMime =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      break;
    case "zip":
      fileTypeMime = "application/zip";
      break;
    case "7z":
      fileTypeMime = "application/x-7z-compressed";
      break;
  }
  return fileTypeMime;
};

// 浏览器全屏
export function reqFullScreen() {
  if (document.documentElement.requestFullscreen) {
    document.documentElement.requestFullscreen();
  }
}

// 浏览器退出全屏
export function exitFullScreen() {
  if (document.exitFullscreen) {
    document.exitFullscreen();
  }
}

// 转换ListPage查询参数
/* 数组转换成字符串逗号拼接
选择下拉框转换成key: value */
export function convertFieldsAndGroup(params: {
  state: any;
  fields: string[];
  group?: string[];
}) {
  const { state, fields, group } = params;
  const obj = deepClone(state);
  for (const key in obj) {
    for (const fKey of fields) {
      if (key === fKey && obj[key]) {
        obj[key] = obj[key].join(",");
      }
    }

    if (group) {
      for (const aKey of group) {
        if (key === aKey) {
          const [itemKey, itemValue] = obj[key];
          obj[itemKey] = itemValue;
          delete obj[key];
        }
      }
    }

    if (obj[key] === "") {
      delete obj[key];
    }
  }

  return obj;
}

export function formatTimeStamp(time: number) {
  const seconds = (time / 1000).toFixed(1);
  const minutes = (time / (1000 * 60)).toFixed(1);
  const hours = (time / (1000 * 60 * 60)).toFixed(1);
  const days = (time / (1000 * 60 * 60 * 24)).toFixed(1);
  if (+seconds < 60) return seconds + " 秒";
  else if (+minutes < 60) return minutes + " 分";
  else if (+hours < 24) return hours + " 时";
  else return days + " 天";
}

export function base64ImgtoFile(dataurl: string, filename = "file") {
  //将base64格式分割：['data:image/png;base64','XXXX']
  const arr: any = dataurl.split(",");
  // .*？ 表示匹配任意字符到下一个符合条件的字符 刚好匹配到：
  // image/png
  const mime = arr[0].match(/:(.*?);/)[1]; //image/png
  //[image,png] 获取图片类型后缀
  const suffix = mime.split("/")[1]; //png
  const bstr = atob(arr[1]); //atob() 方法用于解码使用 base-64 编码的字符串
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], `${filename}.${suffix}`, {
    type: mime
  });
}

// antd-vue modal组件自定义拖拽
export function useAntdModalDragEvent() {
  const header = document.querySelector(".ant-modal-header") as HTMLElement;
  const model = document.querySelector(".ant-modal") as HTMLElement;
  const footer = document.querySelector(".ant-modal-footer") as HTMLElement;
  if (!header || !model) {
    return;
  }
  header.addEventListener("mouseover", () => {
    header.style.cursor = "move";
  });
  footer.addEventListener("mouseover", () => {
    footer.style.cursor = "move";
  });

  const dragEventListener = (dom: HTMLElement) => {
    dom.addEventListener("mousedown", function (e: MouseEvent) {
      const x = e.pageX - model.offsetLeft;
      const y = e.pageY - model.offsetTop;
      function move(e: MouseEvent) {
        model.style.position = "absolute";
        model.style.left = e.pageX - x + "px";
        model.style.top = e.pageY - y + "px";
      }
      document.addEventListener("mousemove", move);
      dom.addEventListener("mouseup", function () {
        document.removeEventListener("mousemove", move);
      });
    });
  };
  dragEventListener(header);
  dragEventListener(footer);
}

export function getFileBase64(file: File) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
}

// 去除字符串首尾空格
export function trimOuterSpace(str: string) {
  let startIndex = 0;
  let endIndex = str.length - 1;

  // 找到第一个非空格字符的位置
  while (startIndex <= endIndex && str[startIndex] === " ") {
    startIndex++;
  }

  // 找到最后一个非空格字符的位置
  while (endIndex >= startIndex && str[endIndex] === " ") {
    endIndex--;
  }

  return str.substring(startIndex, endIndex + 1);
}

// MD5加密
export function MD5Encryption(str: string) {
  return CryptoJS.MD5(str).toString();
}

// file转array
export function fileTransformArray(file: File) {
  return new Promise<any[]>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = xlsx.read(data, { type: "array" });
        const sheetList = workbook.SheetNames;
        //存放json数组格式的表格数据
        sheetList.forEach((item) => {
          const worksheet = workbook.Sheets[item];
          const result = xlsx.utils.sheet_to_json(worksheet);
          resolve(result);
        });
      } catch (err: any) {
        reject(err);
      }
    }; // 成功回调
    reader.onerror = (err) => {
      reject(err);
    }; // 失败回调
    reader.readAsArrayBuffer(file);
  });
}

// CryptoJS 加密
export function encryptText(text: string, key = "simpleKey") {
  const encrypted = CryptoJS.AES.encrypt(text, key).toString();
  return encrypted;
}

// CryptoJS 解密
export function decryptText(encryptedText: string, key = "simpleKey") {
  const decrypted = CryptoJS.AES.decrypt(encryptedText, key).toString(
    CryptoJS.enc.Utf8
  );
  return decrypted;
}

// 生成随机浅色颜色字符串
export function generateRandomLightColor() {
  const letters = "0123456789ABCDEF";
  let color = "#";
  for (let i = 0; i < 3; i++) {
    color += letters[Math.floor(Math.random() * 4) + 8];
  }
  return color;
}

// 随机生成深色颜色字符串
export function generateRandomDarkColor() {
  const letters = "0123456789ABCDEF";
  let color = "#";
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
}

/**
 * 数字添加千分位，考虑小数情况
 */
export function formatNumber(number: number) {
  if (window.isNaN(number)) {
    return number;
  }
  // 将数字转换为字符串
  const numberStr = number.toString();
  // 分割整数部分和小数部分
  const parts = numberStr.split(".");
  // 格式化整数部分
  const integerPart = parts[0].replace(/\d(?=(\d{3})+$)/g, function (match) {
    return match + ",";
  });
  // 整合整数部分和小数部分
  let formattedNumber = integerPart;
  if (parts.length > 1) {
    formattedNumber += "." + parts[1];
  }
  return formattedNumber;
}

// 数字添加千分位
export function toThousands(num: number, digit?: number) {
  // return (num || 0).toString().replace(/(\d)(?=(?:\d{3})+$)/g, '$1,');
  const val = +num;
  if (isNaN(val)) return "-";
  const str = val.toFixed(digit || 0);
  const reg =
    str.indexOf(".") > -1 ? /(\d)(?=(\d{3})+\.)/g : /(\d)(?=(?:\d{3})+$)/g;
  return str.replace(reg, "$1,");
}

// 数字添加百分比
export function toPercentile(num: number, digit?: number) {
  const val = +num;
  if (isNaN(val)) return "-";
  return (val * 100).toFixed(digit || 2) + "%";
}

// YYYY-MM-DD -> timetamp
export const getTimestampFromStr = (str: string) => {
  return dayjs(str).startOf("day").valueOf();
};
// timetamp -> YYYY-MM-DD
export const getStrFromTimestamp = (timestamp: number) => {
  return dayjs(timestamp).format("YYYY-MM-DD");
};
// dayjs -> timetamp
export const getTimestampFromDayjs = (date: Dayjs) => {
  return date.startOf("day").valueOf();
};
// dayjs->YYYY-MM-DD
export const getStrFromDayjs = (date: Dayjs) => {
  return date.format("YYYY-MM-DD");
};
// 获取两个日期之间的所有日期
export const getDaysBetween = (range: [Dayjs, Dayjs]) => {
  const [startDate, endDate] = range;
  const days: string[] = [];
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (start.isAfter(end)) return days;
  let current = start;
  while (current.isBefore(end) || current.isSame(end)) {
    days.push(current.format("YYYY-MM-DD"));
    current = current.add(1, "day");
  }
  return days;
};

// 生成唯一Guid
export function generateGuid() {
  let e = new Date().getTime();
  let c =
    (typeof performance !== "undefined" &&
      performance.now &&
      performance.now() * 1000) ||
    0;
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (g) {
    let d = Math.random() * 16;
    if (e > 0) {
      d = (e + d) % 16 | 0;
      e = Math.floor(e / 16);
    } else {
      d = (c + d) % 16 | 0;
      c = Math.floor(c / 16);
    }
    return (g === "x" ? d : (d & 3) | 8).toString(16);
  });
}

// 获取当前时区
export function getTimeZone() {
  let d = new Date().getTimezoneOffset();
  const c = d < 0 ? "+" : "-";
  d = Math.abs(d);
  return "UTC" + c + ((d / 60) | 0);
}

// 清除与CAS登陆系统的会话session
export function clearCASSession() {
  const loginPageInPro = "https://pac-bop.tec-do.com/#/login";
  const loginPageInTest = "https://test01-bop-front.tec-develop.cn/#/login";
  const account = useAccount();
  const loginPage = account.isProduct ? loginPageInPro : loginPageInTest;
  const serviceStr = encodeURIComponent(loginPage);
  const closeSessionApi = `https://cas.tec-do.com/cas/logout?service=${serviceStr}`;
  try {
    location.href = closeSessionApi;
    console.log("清除session成功");
  } catch (error) {
    console.log("清除session缓存失败：", error);
  }
}
